import type { ApiError } from '@/lib/api-client';
import { usePreviewEnvelope } from './use-preview-envelope';
import { mapStatusToBuilderStep, type BookBuilderStep } from './status-to-step';
import type { PreviewEnvelope } from './types';

/**
 * P5-4 Commit B — re-entry composed hook.
 *
 * Reads the order via the Commit A api layer (`usePreviewEnvelope`),
 * derives the builder step via `mapStatusToBuilderStep`, returns
 * both the loading / error signals and the derived step.
 *
 * "Feeds the Schrittleiste" per the P5-4 Commit B brief item 9: the
 * caller passes `.step` into `<Schrittleiste step={step} />`. When
 * `.step` is null (loading or error), the caller does not render the
 * Schrittleiste — the Kopfleiste stays visible on its own.
 *
 * Deliberately holds no additional state and runs no additional
 * effects. Composition is one call each, both synchronous readers of
 * upstream state:
 *   1. usePreviewEnvelope: owns the fetch state (useState +
 *      useEffect with async-callback-only setState per the
 *      `react-hooks/set-state-in-effect` rule).
 *   2. mapStatusToBuilderStep: pure, no state.
 *
 * No new setState in an effect body exists here. The three
 * pre-existing baseline violations at
 * `pwa/src/components/patterns/RetrofitGenderSheet.tsx:56`,
 * `pwa/src/features/story/use-story-detail.ts:52`,
 * `pwa/src/routes/catalog/CatalogWorldRoute.tsx:110` are the SAME
 * three before and after this commit — see tracker row
 * `[pwa-lint-debt-set-state-in-effect]`.
 */

export interface UseReentryStepState {
  readonly step: BookBuilderStep | null;
  readonly envelope: PreviewEnvelope | null;
  readonly loading: boolean;
  readonly error: ApiError | null;
  readonly refetch: () => void;
}

export function useReentryStep(orderId: string | null): UseReentryStepState {
  const envelope = usePreviewEnvelope(orderId);
  const step = envelope.data ? mapStatusToBuilderStep(envelope.data.status) : null;
  return {
    step,
    envelope: envelope.data,
    loading: envelope.loading,
    error: envelope.error,
    refetch: envelope.refetch,
  };
}
